import { useEffect, useState } from 'react';

import type { NoteList } from '@/shared/api/protocol-ts/model/dto_scribe_catalog_pb';
import { noteListsApi } from '@/shared/api/transcriptor';
import { useAppSelector } from '@/shared/hooks';

import { organizationModel } from '@/entities/organization';
import type { DocumentTemplate } from '@/entities/templates';

const getReferencedListIds = (template: DocumentTemplate | undefined) => [
  ...new Set(
    (template?.sections ?? []).flatMap((section) =>
      section.Lines.flatMap((line) =>
        line.Blocks.flatMap((block) =>
          block.Kind.case === 'ListRef' ? [block.Kind.value.ListID] : [],
        ),
      ),
    ),
  ),
];

// The shared lists a block template references, read once per template:
// list blocks offer their items.
export const useNoteLists = (template: DocumentTemplate | undefined) => {
  const currentOrganizationId = useAppSelector(
    organizationModel.selectors.selectCurrentOrganizationID,
  );
  const organizationId = template?.organization_id || currentOrganizationId;
  const listKey = getReferencedListIds(template).join('\n');
  const [lists, setLists] = useState<NoteList[]>([]);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!listKey || !organizationId) {
      return;
    }
    let active = true;
    setFailed(false);
    noteListsApi
      .list({ organizationId, ids: listKey.split('\n') })
      .then((loaded) => {
        if (active) {
          setLists(loaded);
        }
      })
      .catch(() => {
        if (active) {
          setFailed(true);
        }
      });
    return () => {
      active = false;
    };
  }, [listKey, organizationId]);

  return { lists, failed };
};

// Every active list of the organization in one language, for the template
// editor's list blocks; `reload` reads them again after the lists change.
export const useOrganizationNoteLists = (language: string) => {
  const organizationId = useAppSelector(
    organizationModel.selectors.selectCurrentOrganizationID,
  );
  const [lists, setLists] = useState<NoteList[]>([]);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!organizationId) {
      return;
    }
    let active = true;
    setFailed(false);
    noteListsApi
      .list({ organizationId, language: language || undefined })
      .then((loaded) => {
        if (active) {
          setLists(loaded);
        }
      })
      .catch(() => {
        if (active) {
          setFailed(true);
        }
      });
    return () => {
      active = false;
    };
  }, [organizationId, language, attempt]);

  return {
    organizationId,
    lists,
    failed,
    reload: () => setAttempt((current) => current + 1),
  };
};
